import { useEffect, useState } from "react";
import { Form, InputGroup, Button, Alert } from "react-bootstrap";
import UlpsPortalApiClient from "../UlpsPortalApiClient";

export default function FeatureForm() {
  const NAME_MIN = 3;
  const NAME_MAX = 100;
  const DESC_MAX = 512;
  const FORBIDDEN_NAMES = ["None", "none", "null", "undefined"];

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [registeredFeatures, setRegisteredFeatures] = useState([]);
  const [loadingFeatures, setLoadingFeatures] = useState(true);
  const [licenceSchemas, setLicenceSchemas] = useState([]);
  const [selectedLicenceSchemas, setSelectedLicenceSchemas] = useState([]);

  useEffect(() => {
    Promise.all([
      UlpsPortalApiClient.getFeatures(),
      UlpsPortalApiClient.getLicenceSchemas(),
    ])
      .then(([features, schemas]) => {
        const names = features.map((f) =>
          f.name.trim().toLowerCase()
        );
        setRegisteredFeatures(names);
        setLicenceSchemas(schemas || []);
      })
      .catch((e) => {
        console.error("Failed to fetch feature form data.", e);
      })
      .finally(() => {
        setLoadingFeatures(false);
      });
  }, []);

  const validate = () => {
    if (loadingFeatures) {
      return "Validating existing features, please wait.";
    }

    const trimmedName = name.trim().toLowerCase();
    const trimmedDescription = description.trim();

    if (!trimmedName) {
      return "Feature name is required.";
    }

    if (registeredFeatures.includes(trimmedName)) {
      return `Feature name "${name.trim()}" is already registered.`;
    }

    if (!/[a-zA-Z0-9]/.test(trimmedName)) {
      return "Feature name must contain at least one letter or number.";
    }

    if (trimmedName.length < NAME_MIN) {
      return `Feature name must be at least ${NAME_MIN} characters.`;
    }

    if (trimmedName.length > NAME_MAX) {
      return `Feature name must be at most ${NAME_MAX} characters.`;
    }

    if (trimmedDescription.length > DESC_MAX) {
      return `Description must be at most ${DESC_MAX} characters.`;
    }

    if (selectedLicenceSchemas.length === 0) {
      return "At least one product must be selected.";
    }

    return null;
  };

  const create = async () => {
      setError("");
      setSuccess("");

      const validationError = validate();
      if (validationError) {
        setError(validationError);
        return;
      }

      setSaving(true);

      if(FORBIDDEN_NAMES.includes(name.trim().toLowerCase())) {
        setError(`Feature name "${name.trim()}" is not allowed.`);
        setSaving(false);
        return;
      }
      
      try {
        const payload = {
          name: name.trim(),
          description: description.trim(),
          licenceSchemas: selectedLicenceSchemas,
        };

        const result = await UlpsPortalApiClient.createFeature(payload);

        window.location.hash = "/features?success=true";

      } catch (e) {
        setError("Failed to create feature.");
        console.error(e);
      } finally {
        setSaving(false);
      }
    };

  return (
    <div className="d-flex justify-content-center align-items-start">
      <div style={{ maxWidth: "900px", width: "100%" }}>
        <h2>Create Feature</h2>
        <br />

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <InputGroup className="mb-2">
          <InputGroup.Prepend>
            <InputGroup.Text>Feature Name</InputGroup.Text>
          </InputGroup.Prepend>
          <Form.Control
            type="text"
            value={name}
            maxLength={NAME_MAX}
            minLength={NAME_MIN}
            placeholder="Enter feature name"
            isInvalid={FORBIDDEN_NAMES.includes(name.trim().toLowerCase())}
            onChange={(e) => setName(e.target.value)}
          />
        </InputGroup>
        <InputGroup className="mb-2">
          <InputGroup.Prepend>
            <InputGroup.Text>Description</InputGroup.Text>
          </InputGroup.Prepend>
          <Form.Control
            as="textarea"
            rows={3}
            maxLength={DESC_MAX}
            value={description}
            placeholder="Short description"
            onChange={(e) => setDescription(e.target.value)}
          />
        </InputGroup>
        <InputGroup className="mb-2">
          <InputGroup.Prepend>
            <InputGroup.Text>Products</InputGroup.Text>
          </InputGroup.Prepend>
          <Form.Control
            as="select"
            value=""
            onChange={(e) => {
              const schema = e.target.value;
              if (!schema || selectedLicenceSchemas.includes(schema)) return;
              setSelectedLicenceSchemas((prev) => [...prev, schema]);
            }}
          >
            <option value="">Select a product...</option>
            {licenceSchemas
              .filter((schema) => !selectedLicenceSchemas.includes(schema.name))
              .map((schema) => (
                <option key={schema.name} value={schema.name}>
                  {schema.name}
                </option>
              ))}
          </Form.Control>
        </InputGroup>

        <InputGroup className="mb-2">
          <InputGroup.Prepend>
            <InputGroup.Text>Current Products</InputGroup.Text>
          </InputGroup.Prepend>
          <div className="form-control" style={{ minHeight: "50px", height: "auto" }}>
            {selectedLicenceSchemas.length > 0 ? (
              selectedLicenceSchemas.map((schema) => (
                <span
                  key={schema}
                  style={{
                    padding: "4px 10px",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    marginRight: "6px",
                    borderRadius: "6px",
                    border: "1px solid #ddd",
                    backgroundColor: "#eef4ff",
                  }}
                  onClick={() => setSelectedLicenceSchemas((prev) => prev.filter((item) => item !== schema))}
                >
                  {schema}
                  <span style={{ fontWeight: "bold", fontSize: "18px", color: "#dc3545" }}>×</span>
                </span>
              ))
            ) : (
              <span style={{ color: "#6c757d" }}>No products selected</span>
            )}
          </div>
        </InputGroup>

        <Button
          variant="success"
          onClick={create}
          disabled={saving || loadingFeatures}
        >
          {saving ? "Saving..." : "Create Feature"}
        </Button>

        <Button
          variant="secondary"
          className="ml-2"
          onClick={() => (window.location.href = "#/features")}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
